import {
  getProjects,
  updateProject,
  deleteProject,
  addTeams,
  deleteTeams,
  addUsers,
  deleteUsers,
} from '@/api/project';
import { v4 as uuidv4 } from 'uuid';
import { AccountModule, getPermissionNames } from '@/store/modules/account';
import moment from 'moment';
import { companyActiveTeam } from '@/utils/team';
import { companyAllActiveUser } from '@/utils/user';
import { getCompanyAllProject } from '@/utils/project';

type IProjectInfo = IProject.IResponse.IProjectInfo;
type IUpdateProjectInfo = IProject.IRequest.IUpdateProjectInfo;

interface ITableProjectInfo extends IProjectInfo {
  hasEditPermission: boolean;
}
interface IProjectManage {
  data: IProjectInfo[];
  // defaultParams: IProject.IRequest.IGetProject;
  editData: {
    projectInfo: IProjectInfo | null;
    isCreate: boolean;
  };
  requestGetProjects(): Promise<string>;
  requestUpdateProject(params: IUpdateProjectInfo): Promise<string>;
  requestDeleteProject(projectId: string): Promise<string>;
  requestAddTeams(params: string[]): Promise<string>;
  requestDeleteTeams(params: string[]): Promise<string>;
  requestAddUsers(params: string[]): Promise<string>;
  requestDeleteUsers(params: string[]): Promise<string>;
  createProject(): void;
  editProject(project: IProjectInfo): void;
  updateProjectList(params: IUpdateProjectInfo): void;
  addProject(project: IProjectInfo): void;
  updateProject(project: IProjectInfo): void;
  removeProject(projectId: string): void;
  addProjectTeams(teamIds: string[]): void;
  removeProjectTeams(teamIds: string[]): void;
  addProjectUsers(userIds: string[]): void;
  removeProjectUsers(userIds: string[]): void;
}

export class ProjectManage implements IProjectManage {
  private $data: IProjectInfo[] = [];
  private $editData: {
    projectInfo: IProjectInfo | null;
    isCreate: boolean;
  } = {
    projectInfo: null,
    isCreate: false,
  };
  private defaultParams = {
    returnTeam: true,
    returnManager: true,
  };
  get data() {
    return this.$data;
  }
  get tableData(): ITableProjectInfo[] {
    return enrichProjectInfo(this.$data);
  }
  get editData() {
    return this.$editData;
  }
  public requestGetProjects(): Promise<string> {
    this.defaultParams = {
      returnTeam: true,
      returnManager: true,
    };
    return new Promise((resolve, reject) => {
      getProjects(this.defaultParams).then((res) => {
        this.$data = res.data;
        resolve(res.message);
      }).catch((err) => {
        reject(err.message);
      });
    });
  }
  public requestUpdateProject(params: IUpdateProjectInfo): Promise<string> {
    return new Promise((resolve, reject) => {
      updateProject({
        projectId: this.$editData.projectInfo!.id,
        data: params,
      }).then((res) => {
        this.updateProjectList(params);
        resolve(res.message);
        getCompanyAllProject(true);
      }).catch((err) => {
        reject(err.message);
      });
    });
  }
  public requestDeleteProject(projectId: string): Promise<string> {
    return new Promise((resolve, reject) => {
      deleteProject(projectId).then((res) => {
        this.removeProject(projectId);
        resolve(res.message);
        getCompanyAllProject(true);
      }).catch((err) => {
        reject(err.message);
      });
    });
  }
  public requestAddTeams(teamIds: string[]): Promise<string> {
    return new Promise((resolve, reject) => {
      addTeams({
        projectId: this.$editData.projectInfo!.id,
        teamIds,
      }).then((res) => {
        this.addProjectTeams(teamIds);
        resolve(res.message);
      }).catch((err) => {
        reject(err.message);
      });
    });
  }
  public requestDeleteTeams(teamIds: string[]): Promise<string> {
    return new Promise((resolve, reject) => {
      deleteTeams({
        projectId: this.$editData.projectInfo!.id,
        teamIds,
      }).then((res) => {
        this.removeProjectTeams(teamIds);
        resolve(res.message);
      }).catch((err) => {
        reject(err.message);
      });
    });
  }
  public requestAddUsers(userIds: string[]): Promise<string> {
    return new Promise((resolve, reject) => {
      addUsers({
        projectId: this.$editData.projectInfo!.id,
        userIds,
      }).then((res) => {
        this.addProjectUsers(userIds);
        resolve(res.message);
      }).catch((err) => {
        reject(err.message);
      });
    });
  }
  public requestDeleteUsers(userIds: string[]): Promise<string> {
    return new Promise((resolve, reject) => {
      deleteUsers({
        projectId: this.$editData.projectInfo!.id,
        userIds,
      }).then((res) => {
        this.removeProjectUsers(userIds);
        resolve(res.message);
      }).catch((err) => {
        reject(err.message);
      });
    });
  }
  public createProject() {
    this.$editData = {
      projectInfo: {
        id: uuidv4(),
        name: '',
        description: '',
        active: true,
        createdName: '',
        createdBy: '',
        createdOn: '',
        teams: [],
        managers: [],
      },
      isCreate: true,
    };
  }
  public editProject(projectInfo: IProjectInfo) {
    this.$editData = {
      projectInfo: {...projectInfo},
      isCreate: false,
    };
  }
  public addProject(projectInfo: IProjectInfo) {
    this.$data.unshift(projectInfo);
  }
  public updateProject(projectInfo: IProjectInfo) {
    for (const projectItem of this.$data) {
      if (projectItem.id === projectInfo.id) {
        Object.assign(projectItem, projectInfo);
        break;
      }
    }
  }
  public updateProjectList(project: IUpdateProjectInfo) {
    const newProject: IProjectInfo = {
      id: this.$editData.projectInfo!.id,
      name: project.name,
      description: project.description,
      active: project.active,
      createdName: this.$editData.projectInfo!.createdName || AccountModule.name,
      createdOn: this.$editData.projectInfo!.createdOn || moment().toJSON(),
      createdBy: this.$editData.projectInfo!.createdBy || AccountModule.userDetailInfo?.id || '',
      teams: this.$editData.projectInfo!.teams,
      managers: this.$editData.projectInfo!.managers,
    };
    if (this.$editData.isCreate) {
      this.addProject(newProject);
    } else {
      this.updateProject(newProject);
    }
  }
  public removeProject(projectId: string) {
    const index = this.$data.findIndex((project) => project.id === projectId);
    this.$data.splice(index, 1);
  }
  public addProjectTeams(teamIds: string[]) {
    const projectId = this.$editData.projectInfo!.id;
    const newTeams = companyActiveTeam.filter((team) => teamIds.includes(team.id));
    for (const project of this.$data) {
      if (project.id === projectId) {
        project.teams = project.teams.concat(newTeams);
        break;
      }
    }
  }
  public removeProjectTeams(teamIds: string[]) {
    const projectId = this.$editData.projectInfo!.id;
    const newTeams = this.$editData.projectInfo!.teams.filter((team) => !teamIds.includes(team.id));
    for (const project of this.$data) {
      if (project.id === projectId) {
        project.teams = newTeams;
        break;
      }
    }
  }
  public addProjectUsers(userIds: string[]) {
    const projectId = this.$editData.projectInfo!.id;
    const newManagers = companyAllActiveUser.filter((user) => userIds.includes(user.id));
    for (const project of this.$data) {
      if (project.id === projectId) {
        project.managers = project.managers.concat(newManagers);
        break;
      }
    }
  }
  public removeProjectUsers(userIds: string[]) {
    const projectId = this.$editData.projectInfo!.id;
    const newManagers = this.$editData.projectInfo!.managers.filter((team) => !userIds.includes(team.id));
    for (const project of this.$data) {
      if (project.id === projectId) {
        project.managers = newManagers;
        break;
      }
    }
  }
  public clearData() {
    this.data.length = 0;
  }
}

const hasEditPermission = (project: IProjectInfo, permissionNames: string[]) => {
  let editAll = false;
  if (permissionNames.includes('Project_Edit_All')) {
    editAll = true;
    return editAll;
  }
  let editAdmin = false;
  if (permissionNames.includes('Project_Edit_Admin')) {
    editAdmin = project.managers.some((manager) => manager.id === AccountModule.userDetailInfo?.id);
  }
  let editOwn = false;
  if (permissionNames.includes('Project_Edit_Own')) {
    editOwn = project.createdBy === AccountModule.userDetailInfo?.id;
  }
  return editAll || editAdmin || editOwn;
};
const enrichProjectInfo = (projects: IProjectInfo[]): ITableProjectInfo[] => {
  const result: ITableProjectInfo[] = [];
  for (const project of projects) {
    result.push({
      ...project,
      hasEditPermission: hasEditPermission(project, getPermissionNames()),
    });
  }
  return result;
};
export const projectManage = new ProjectManage();
